import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Icon,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Select,
    Switch,
    useDisclosure,
} from '@chakra-ui/react';
import { useMemo, useRef, useState } from 'react';
import { TbChevronDown } from 'react-icons/tb';
import InventoryTypeaheadField, {
    type TypeaheadOption,
} from './InventoryTypeaheadField';
import QuantityInputField from './QuantityInputField';
import UnitSelectField from './UnitSelectField';
import CurrencyInput from './CurrencyInput';
import { useSearchSuppliers } from '../../hooks/useSearchSuppliers';
import { filterLocations, useGetLocations } from '../../hooks/useGetLocations';
import { useGetInventoryUnits } from '../../hooks/inventory/useGetInventoryUnits';
import type { ImportTargetField } from '../../functions/import/fuzzyColumnMatch';
import type { InventoryUnit } from '../../types/inventory';
import {
    INVENTORY_CATEGORY_OPTIONS,
    INVENTORY_PART_TYPE_OPTIONS,
    formatInventoryUnitLabel,
} from '../../utils/inventoryConstants';

/** Compact table-cell select: opens immediately so edit is one click. */
function ImportInlineSelect({
    value,
    options,
    onChange,
    onCommit,
    onDismiss,
    width,
}: {
    value: string;
    options: Array<{ value: string; label: string }>;
    onChange: (value: string) => void;
    onCommit?: (value: string) => void;
    onDismiss?: () => void;
    width?: string;
}) {
    const committedRef = useRef(false);
    const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
    const selectedLabel =
        options.find((o) => o.value === value)?.label ||
        (value ? value : options[0]?.label ?? '');

    const handleClose = () => {
        if (!isOpen) return;
        onClose();
        if (!committedRef.current) {
            onDismiss?.();
        }
        committedRef.current = false;
    };

    return (
        <Menu isOpen={isOpen} onClose={handleClose} placement="bottom-start" autoSelect={false}>
            <MenuButton
                as={Button}
                size="sm"
                w={width}
                maxW={width}
                minW={width}
                variant="outline"
                fontWeight="normal"
                textAlign="left"
                px={2}
                rightIcon={<Icon as={TbChevronDown as React.ElementType} />}
                overflow="hidden"
            >
                <Box as="span" noOfLines={1} display="block" textAlign="left">
                    {selectedLabel}
                </Box>
            </MenuButton>
            {/* MenuList portals by itself; an extra Portal breaks outside-click
                detection so item clicks look like dismissals. */}
            <MenuList
                maxH="220px"
                overflowY="auto"
                minW="112px"
                zIndex={2000}
                py={1}
            >
                {options.map((opt) => (
                    <MenuItem
                        key={opt.value}
                        fontSize="sm"
                        onClick={() => {
                            committedRef.current = true;
                            onChange(opt.value);
                            onCommit?.(opt.value);
                        }}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </MenuList>
        </Menu>
    );
}

/**
 * Validate Default Values using the same rules as Add Item submit.
 * `confirmedRefs` tracks supplier/location selections that came from the typeahead list.
 */
export function validateInventoryImportDefaults(
    constants: Record<string, string>,
    fieldsNeedingConstant: ImportTargetField[],
    confirmedRefs: Set<string>,
): Record<string, string> {
    const errors: Record<string, string> = {};
    const needing = new Set(fieldsNeedingConstant.map((f) => f.id));

    const stock = Number(constants.initialInStockQuantity ?? '0');
    const stockIsDefault = needing.has('initialInStockQuantity');

    fieldsNeedingConstant.forEach((field) => {
        const raw = (constants[field.id] ?? '').trim();

        switch (field.id) {
            case 'category':
                if (!raw) errors[field.id] = 'Category is required.';
                break;
            case 'manufacturerPartNumber':
                if (!raw) errors[field.id] = 'Primary mfg. part number is required.';
                break;
            case 'additionalPartNumbers':
                break;
            case 'name':
                if (!raw) errors[field.id] = 'Name is required.';
                break;
            case 'partType':
                if (!raw) errors[field.id] = 'Part Type is required.';
                break;
            case 'supplierName':
                if (!raw || !confirmedRefs.has('supplierName')) {
                    errors[field.id] = 'Select a supplier from the list.';
                }
                break;
            case 'locationTitle':
                if (!raw || !confirmedRefs.has('locationTitle')) {
                    errors[field.id] = raw
                        ? 'Location not found.'
                        : 'Location is required.';
                }
                break;
            case 'unit':
                if (!raw) errors[field.id] = 'Unit is required.';
                break;
            case 'unitCost': {
                if (stockIsDefault && stock > 0 && !raw) {
                    errors[field.id] =
                        'Unit Cost is required when In-Stock Quantity is greater than zero.';
                }
                break;
            }
            case 'reorderQuantity':
            case 'targetQuantity': {
                const alertRaw = (constants.restockAlert ?? '').trim().toLowerCase();
                const restockAlert = ['true', 'yes', 'y', '1'].includes(alertRaw);
                if (restockAlert && constants[field.id] === undefined) {
                    errors[field.id] =
                        `${field.label} is required when Restock Alert is on.`;
                }
                break;
            }
            case 'initialInStockQuantity':
                // 0 is a valid quantity; ensure we have a numeric string (QuantityInput sets "0")
                if (constants[field.id] === undefined) {
                    errors[field.id] = `${field.label} is required.`;
                }
                break;
            default:
                if (!raw) errors[field.id] = `${field.label} is required.`;
        }
    });

    return errors;
}

interface InventoryImportDefaultValueFieldProps {
    field: ImportTargetField;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    onRefConfirmed?: (fieldId: string, confirmed: boolean) => void;
    /** When true, omit FormLabel (compact table cells). */
    hideLabel?: boolean;
    /** Called when a discrete option is chosen (select / typeahead pick). */
    onCommit?: (value: string) => void;
    /** Called when an auto-opened inline select is dismissed without a choice. */
    onDismiss?: () => void;
}

/**
 * Renders a single Default Value control matching Add Item drawer widgets.
 * Stores string values for import (`supplierName` / `locationTitle` labels, not ids).
 */
export function InventoryImportDefaultValueField({
    field,
    value,
    onChange,
    error,
    onRefConfirmed,
    hideLabel = false,
    onCommit,
    onDismiss,
}: InventoryImportDefaultValueFieldProps) {
    const [supplierSearch, setSupplierSearch] = useState(value);
    const [locationSearch, setLocationSearch] = useState(value);

    const { data: allSuppliersData, isLoading: isSupplierLoading } = useSearchSuppliers('', {
        listAll: field.id === 'supplierName',
    });
    const { data: locationsData } = useGetLocations();
    const { data: unitsData } = useGetInventoryUnits(field.id === 'unit' && hideLabel);

    const supplierOptions: TypeaheadOption[] = useMemo(() => {
        const all = allSuppliersData?.suppliers ?? [];
        const q = supplierSearch.trim().toLowerCase();
        return all
            .filter((s) => !q || s.title.toLowerCase().includes(q))
            .map((s) => ({ value: s._id, label: s.title }));
    }, [allSuppliersData?.suppliers, supplierSearch]);

    const locationOptions: TypeaheadOption[] = useMemo(() => {
        const selectable = filterLocations(locationsData?.locations ?? [], {
            excludeAll: true,
        });
        const q = locationSearch.trim().toLowerCase();
        return selectable
            .filter((l) => !q || l.title.toLowerCase().includes(q))
            .map((l) => ({ value: l._id, label: l.title }));
    }, [locationsData?.locations, locationSearch]);

    const supplierDisplay = useMemo(() => {
        if (field.id !== 'supplierName') return '';
        const match = supplierOptions.find((o) => o.label === value);
        if (match) return match.label;
        // Prefer the committed value while options are still loading / search is stale.
        if (value) return value;
        return supplierSearch;
    }, [field.id, supplierOptions, value, supplierSearch]);

    const locationDisplay = useMemo(() => {
        if (field.id !== 'locationTitle') return '';
        const match = locationOptions.find((o) => o.label === value);
        if (match) return match.label;
        if (value) return value;
        return locationSearch;
    }, [field.id, locationOptions, value, locationSearch]);

    const wrap = (control: React.ReactNode) => (
        <FormControl isRequired={!hideLabel} isInvalid={!!error}>
            {hideLabel ? null : <FormLabel fontSize="sm">{field.label}</FormLabel>}
            {control}
            {error ? <FormErrorMessage>{error}</FormErrorMessage> : null}
        </FormControl>
    );

    switch (field.id) {
        case 'category':
            if (hideLabel) {
                return wrap(
                    <ImportInlineSelect
                        value={value}
                        width="100%"
                        options={INVENTORY_CATEGORY_OPTIONS.map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                        }))}
                        onChange={onChange}
                        onCommit={onCommit}
                        onDismiss={onDismiss}
                    />,
                );
            }
            return wrap(
                <Select
                    size="sm"
                    value={value || 'part'}
                    onChange={(e) => {
                        const next = e.target.value;
                        onChange(next);
                        onCommit?.(next);
                    }}
                >
                    {INVENTORY_CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </Select>,
            );
        case 'partType':
            if (hideLabel) {
                return wrap(
                    <ImportInlineSelect
                        value={value || 'oem'}
                        width="100%"
                        options={INVENTORY_PART_TYPE_OPTIONS.map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                        }))}
                        onChange={onChange}
                        onCommit={onCommit}
                        onDismiss={onDismiss}
                    />,
                );
            }
            return wrap(
                <Select
                    size="sm"
                    value={value || 'oem'}
                    onChange={(e) => {
                        const next = e.target.value;
                        onChange(next);
                        onCommit?.(next);
                    }}
                >
                    {INVENTORY_PART_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </Select>,
            );
        case 'supplierName':
            return wrap(
                <Box w={hideLabel ? '100%' : undefined}>
                    <InventoryTypeaheadField
                        value={supplierDisplay}
                        onChange={(val) => {
                            const match =
                                supplierOptions.find((o) => o.value === val) ??
                                supplierOptions.find((o) => o.label === val);
                            if (match) {
                                onChange(match.label);
                                setSupplierSearch(match.label);
                                onRefConfirmed?.('supplierName', true);
                                onCommit?.(match.label);
                            } else {
                                // Keep typed text (never wipe to '') so the field
                                // doesn't blank while options load or while searching.
                                setSupplierSearch(val);
                                onChange(val);
                                onRefConfirmed?.('supplierName', false);
                            }
                        }}
                        options={supplierOptions}
                        onSearch={setSupplierSearch}
                        selectedLabel={value || undefined}
                        placeholder="Search…"
                        isLoading={isSupplierLoading}
                    />
                </Box>,
            );
        case 'locationTitle':
            return wrap(
                <Box w={hideLabel ? '100%' : undefined}>
                    <InventoryTypeaheadField
                        value={locationDisplay}
                        onChange={(val) => {
                            const trimmed = val.trim();
                            const match =
                                locationOptions.find((o) => o.value === val) ??
                                locationOptions.find((o) => o.label === trimmed);
                            if (match) {
                                onChange(match.label);
                                setLocationSearch(match.label);
                                onRefConfirmed?.('locationTitle', true);
                                onCommit?.(match.label);
                            } else {
                                setLocationSearch(val);
                                onChange(val);
                                onRefConfirmed?.('locationTitle', false);
                            }
                        }}
                        options={locationOptions}
                        onSearch={setLocationSearch}
                        selectedLabel={value || undefined}
                        placeholder="Search…"
                    />
                </Box>,
            );
        case 'unit':
            if (hideLabel) {
                const units = unitsData?.units ?? [];
                return wrap(
                    <ImportInlineSelect
                        value={units.includes(value as InventoryUnit) ? value : ''}
                        width="100%"
                        options={units.map((unit) => ({
                            value: unit,
                            label: formatInventoryUnitLabel(unit),
                        }))}
                        onChange={(next) => {
                            onChange(next);
                        }}
                        onCommit={onCommit}
                        onDismiss={onDismiss}
                    />,
                );
            }
            return wrap(
                <UnitSelectField
                    value={value}
                    onChange={(unit: InventoryUnit) => {
                        onChange(unit);
                        onCommit?.(unit);
                    }}
                />,
            );
        case 'unitCost':
            return wrap(
                <CurrencyInput
                    value={value === '' ? 0 : Number(value)}
                    onChange={(val) => onChange(String(val))}
                    useThousandsSeparator
                />,
            );
        case 'reorderQuantity':
        case 'targetQuantity':
            return wrap(
                <QuantityInputField
                    value={value === '' ? null : Number(value)}
                    onChange={(val) => onChange(val == null ? '' : String(val))}
                    allowEmpty
                />,
            );
        case 'initialInStockQuantity':
            return wrap(
                <QuantityInputField
                    value={value === '' ? 0 : Number(value)}
                    onChange={(val) => onChange(String(val ?? 0))}
                />,
            );
        case 'restockAlert': {
            const isOn = ['true', 'yes', 'y', '1'].includes(
                (value || 'false').trim().toLowerCase(),
            );
            return wrap(
                <Switch
                    isChecked={isOn}
                    onChange={(e) => {
                        const next = e.target.checked ? 'true' : 'false';
                        onChange(next);
                        onCommit?.(next);
                    }}
                />,
            );
        }
        case 'origin':
            return wrap(
                <Input size="sm" value={value} onChange={(e) => onChange(e.target.value)} />,
            );
        case 'manufacturerPartNumber':
        case 'additionalPartNumbers':
        case 'name':
        default:
            return wrap(
                <Input size="sm" value={value} onChange={(e) => onChange(e.target.value)} />,
            );
    }
}
